import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { tournamentsApi, submissionsApi } from '../services/api';
import { Tournament, DeckSubmission } from '../types';
import DeckSubmissionModal from '../components/DeckSubmissionModal';

const MyTournaments: React.FC = () => {
  const [followedTournaments, setFollowedTournaments] = useState<Tournament[]>([]);
  const [submissions, setSubmissions] = useState<DeckSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [followedTournamentIds, setFollowedTournamentIds] = useState<Set<string>>(new Set());
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [followedResponse, submissionsResponse, tournamentsWithSubmissionsResponse] = await Promise.all([
        tournamentsApi.getFollowed(),
        submissionsApi.getAll(),
        tournamentsApi.getWithSubmissions()
      ]);
      
      setSubmissions(submissionsResponse.data);
      
      // Track followed tournament IDs
      const followedIds = new Set(followedResponse.data.map(t => t.id));
      setFollowedTournamentIds(followedIds);
      
      // Combine followed tournaments and tournaments with submissions, removing duplicates
      const allTournaments = [...followedResponse.data];
      tournamentsWithSubmissionsResponse.data.forEach(tournament => {
        if (!allTournaments.find(t => t.id === tournament.id)) {
          allTournaments.push(tournament);
        }
      });
      setFollowedTournaments(allTournaments);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (tournamentId: string) => {
    try {
      await tournamentsApi.follow(tournamentId);
      setFollowedTournamentIds(prev => new Set([...Array.from(prev), tournamentId]));
    } catch (error: any) {
      console.error('Failed to follow tournament:', error);
      if (error.response?.status === 400 && error.response?.data?.detail === "Already following this tournament") {
        // Already following, just update UI
        setFollowedTournamentIds(prev => new Set([...Array.from(prev), tournamentId]));
      } else {
        alert('Failed to follow tournament. Please try again.');
      }
    }
  };

  const handleUnfollow = async (tournamentId: string) => {
    // Check if user has submissions to this tournament
    const hasSubmissions = submissions.some(s => s.tournament_id === tournamentId);
    
    if (hasSubmissions) {
      const confirmed = window.confirm(
        'You have submitted decks to this tournament. Unfollowing will keep your submissions but hide the tournament from this page until you follow it again. Continue?'
      );
      if (!confirmed) return;
    }
    
    try {
      await tournamentsApi.unfollow(tournamentId);
      setFollowedTournamentIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(tournamentId);
        return newSet;
      });
      
      // If no submissions, remove from display
      if (!hasSubmissions) {
        setFollowedTournaments(prev => prev.filter(t => t.id !== tournamentId));
      }
    } catch (error) {
      console.error('Failed to unfollow tournament:', error);
      alert('Failed to unfollow tournament. Please try again.');
    }
  };

  const handleDeleteSubmission = async (submissionId: string) => {
    if (!window.confirm('Are you sure you want to delete this deck submission? This action cannot be undone.')) {
      return;
    }

    try {
      await submissionsApi.delete(submissionId);
      setSubmissions(prev => 
        prev.filter(s => s.id !== submissionId)
      );
      alert('Deck submission deleted successfully.');
    } catch (error) {
      console.error('Failed to delete submission:', error);
      alert('Failed to delete deck submission. Please try again.');
    }
  };

  const handleSubmitDeck = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    setShowSubmissionModal(true);
  };

  const handleCloseModal = () => {
    setShowSubmissionModal(false);
    setSelectedTournament(null);
  };

  const getSubmissionsForTournament = (tournamentId: string) => {
    return submissions.filter(s => s.tournament_id === tournamentId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading your tournaments...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">My Tournaments</h1>
        
        {followedTournaments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg mb-4">You haven't followed any tournaments or submitted any decks yet.</p>
            <Link 
              to="/tournaments" 
              className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse Tournaments
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {followedTournaments.map((tournament) => {
              const tournamentSubmissions = getSubmissionsForTournament(tournament.id);
              
              return (
                <div key={tournament.id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-800">{tournament.name}</h2>
                      <p className="text-gray-600">{tournament.description}</p>
                      <p className="text-sm text-gray-500">
                        Date: {new Date(tournament.date).toLocaleDateString()}
                      </p>
                      {tournament.circuit && (
                        <p className="text-sm text-gray-500">
                          Circuit: {tournament.circuit.name}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleSubmitDeck(tournament)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        Submit Deck
                      </button>
                      {followedTournamentIds.has(tournament.id) ? (
                        <button
                          onClick={() => handleUnfollow(tournament.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                        >
                          Unfollow
                        </button>
                      ) : (
                        <button
                          onClick={() => handleFollow(tournament.id)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          Follow
                        </button>
                      )}
                    </div>
                  </div>

                  {tournamentSubmissions.length > 0 ? (
                    <div>
                      <h3 className="text-lg font-medium text-gray-700 mb-3">Your Submissions</h3>
                      <div className="space-y-3">
                        {tournamentSubmissions.map((submission) => (
                          <div key={submission.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium text-gray-800">{submission.deck_name}</h4>
                                <p className="text-sm text-gray-600">
                                  Total Cost: €{submission.total_cost.toFixed(2)}
                                  {tournament.ruleset?.budget_limit && (
                                    <span className={`ml-2 px-2 py-1 rounded text-xs ${
                                      submission.total_cost <= tournament.ruleset.budget_limit
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {submission.total_cost <= tournament.ruleset.budget_limit
                                        ? 'Within Budget'
                                        : 'Over Budget'}
                                    </span>
                                  )}
                                </p>
                                <p className="text-sm text-gray-500">
                                  Submitted: {new Date(submission.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}>
                                  {submission.status}
                                </span>
                                <Link
                                  to={`/deck/${submission.id}`}
                                  className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                                >
                                  View Details
                                </Link>
                                <button
                                  onClick={() => handleDeleteSubmission(submission.id)}
                                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                            
                            {submission.rejection_reason && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                <p className="text-sm text-red-800">
                                  <strong>Rejection Reason:</strong> {submission.rejection_reason}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-600 mb-2">No submissions yet</p>
                      <button
                        onClick={() => handleSubmitDeck(tournament)}
                        className="inline-block bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Submit Your First Deck
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Deck Submission Modal */}
      {selectedTournament && (
        <DeckSubmissionModal
          tournament={selectedTournament}
          isOpen={showSubmissionModal}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default MyTournaments;
